import { Form, Formik } from 'formik';
import { array, boolean, number, object, string } from 'yup';

import {
  Environment,
  EnvironmentId,
  EnvironmentType,
} from '@/react/portainer/environments/types';
import {
  Query,
  useEnvironmentList,
} from '@/react/portainer/environments/queries/useEnvironmentList';
import { useNamespacesQuery } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';
import { NamespaceSelect } from '@/react/portainer/policies/components/kubernetes/rbac/NamespaceSelect';
import { useCreateScopedKubeconfigMutation } from '@/react/portainer/scoped-kubeconfigs/queries/useCreateScopedKubeconfigMutation';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { InlineLoader } from '@@/InlineLoader';

import { saveScopedKubeconfigFile } from './kubeconfig.service';

const KUBE_ENVIRONMENT_TYPES = [
  EnvironmentType.KubernetesLocal,
  EnvironmentType.AgentOnKubernetes,
  EnvironmentType.EdgeAgentOnKubernetes,
];

const MAX_EXPIRY_SECONDS = 7 * 24 * 3600;

const EXPIRY_OPTIONS = [
  { label: '1 hour', value: 3600 },
  { label: '4 hours', value: 4 * 3600 },
  { label: '1 day', value: 24 * 3600 },
  { label: '7 days (maximum)', value: MAX_EXPIRY_SECONDS },
];

interface ScopedKubeconfigFormValues {
  environmentId: EnvironmentId;
  namespaces: string[];
  includeSecrets: boolean;
  expirySeconds: number;
  description: string;
}

const validationSchema = object({
  environmentId: number().required(),
  namespaces: array(string().required()).defined(),
  includeSecrets: boolean().required(),
  expirySeconds: number()
    .required('Expiry is required')
    .positive('Expiry must be greater than 0')
    .max(MAX_EXPIRY_SECONDS, 'Expiry cannot exceed 7 days'),
  description: string(),
});

interface Props {
  envQueryParams: Query;
  onClose: () => void;
}

export function ScopedKubeconfigForm({ envQueryParams, onClose }: Props) {
  const { environments, isLoading } = useEnvironmentList({
    ...envQueryParams,
    page: 1,
    types: KUBE_ENVIRONMENT_TYPES,
  });

  const availableEnvironments = environments.filter((env) => env.Status <= 2);

  if (isLoading) {
    return (
      <Modal.Body>
        <InlineLoader>Loading environments...</InlineLoader>
      </Modal.Body>
    );
  }

  if (availableEnvironments.length === 0) {
    return (
      <Modal.Body>
        <TextTip>No available Kubernetes environments were found.</TextTip>
      </Modal.Body>
    );
  }

  return (
    <ScopedKubeconfigFormFields
      environments={availableEnvironments}
      onClose={onClose}
    />
  );
}

function ScopedKubeconfigFormFields({
  environments,
  onClose,
}: {
  environments: Environment[];
  onClose: () => void;
}) {
  const createMutation = useCreateScopedKubeconfigMutation();

  const initialValues: ScopedKubeconfigFormValues = {
    environmentId: environments[0].Id,
    namespaces: [],
    includeSecrets: false,
    expirySeconds: EXPIRY_OPTIONS[2].value,
    description: '',
  };

  return (
    <Formik<ScopedKubeconfigFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, setFieldValue }) => (
        <Form>
          <Modal.Body>
            <FormControl
              label="Environment"
              inputId="scoped-kubeconfig-environment"
              size="vertical"
              required
            >
              <PortainerSelect
                inputId="scoped-kubeconfig-environment"
                value={values.environmentId}
                onChange={(id) => {
                  setFieldValue('environmentId', id);
                  setFieldValue('namespaces', []);
                }}
                options={environments.map((env) => ({
                  label: env.Name,
                  value: env.Id,
                }))}
                data-cy="scoped-kubeconfig-environment-select"
              />
            </FormControl>

            <FormControl
              label="Namespaces"
              inputId="scoped-kubeconfig-namespaces"
              size="vertical"
              tooltip="Leave empty to include every namespace you have access to, excluding portainer namespaces."
            >
              <NamespacesField
                environmentId={values.environmentId}
                selectedNamespaces={values.namespaces}
                onChange={(namespaces) =>
                  setFieldValue('namespaces', namespaces)
                }
              />
            </FormControl>

            <SwitchField
              label="Include secrets"
              tooltip="Allow this kubeconfig to read Secrets in the selected namespaces."
              checked={values.includeSecrets}
              onChange={(checked) => setFieldValue('includeSecrets', checked)}
              data-cy="scoped-kubeconfig-include-secrets"
            />

            <FormControl
              label="Expires in"
              inputId="scoped-kubeconfig-expiry"
              size="vertical"
              required
              errors={errors.expirySeconds}
            >
              <PortainerSelect
                inputId="scoped-kubeconfig-expiry"
                value={values.expirySeconds}
                onChange={(value) =>
                  setFieldValue(
                    'expirySeconds',
                    value ?? EXPIRY_OPTIONS[0].value
                  )
                }
                options={EXPIRY_OPTIONS}
                data-cy="scoped-kubeconfig-expiry-select"
              />
            </FormControl>

            <FormControl
              label="Description"
              inputId="scoped-kubeconfig-description"
              size="vertical"
              tooltip="Optional note to help identify this kubeconfig later."
            >
              <Input
                id="scoped-kubeconfig-description"
                value={values.description}
                onChange={(e) => setFieldValue('description', e.target.value)}
                placeholder="e.g. CI pipeline access"
                data-cy="scoped-kubeconfig-description-input"
              />
            </FormControl>
          </Modal.Body>

          <Modal.Footer>
            <Button
              onClick={onClose}
              color="default"
              type="button"
              disabled={createMutation.isLoading}
              data-cy="cancel-kubeconfig-download-button"
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              isLoading={createMutation.isLoading}
              loadingText="Creating..."
              data-cy="download-scoped-kubeconfig-button"
            >
              Download File
            </LoadingButton>
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  );

  function handleSubmit(values: ScopedKubeconfigFormValues) {
    const environment = environments.find(
      (env) => env.Id === values.environmentId
    );
    if (!environment) {
      return;
    }

    createMutation.mutate(
      {
        environmentId: values.environmentId,
        namespaces: values.namespaces,
        includeSecrets: values.includeSecrets,
        expirySeconds: values.expirySeconds,
        description: values.description || undefined,
      },
      {
        onSuccess: (response) => {
          if (response?.kubeconfig) {
            saveScopedKubeconfigFile(response.kubeconfig, environment.Name);
          }
          onClose();
        },
      }
    );
  }
}

function NamespacesField({
  environmentId,
  selectedNamespaces,
  onChange,
}: {
  environmentId: EnvironmentId;
  selectedNamespaces: string[];
  onChange: (namespaces: string[]) => void;
}) {
  const namespacesQuery = useNamespacesQuery(environmentId, {
    select: (namespaces) => namespaces.map((namespace) => namespace.Name),
  });

  return (
    <NamespaceSelect
      namespaces={namespacesQuery.data ?? []}
      selectedNamespaces={selectedNamespaces}
      onChange={onChange}
      data-cy="scoped-kubeconfig-namespaces-select"
    />
  );
}
